import { ChangeEvent, MouseEvent, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Box,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  TextField,
  MenuItem,
  DialogActions,
  Link,
  Tooltip,
  IconButton,
  Menu,
} from '@mui/material';
import Page from 'src/components/Page';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { HttpAgent } from 'src/api/agent';
import { capitalCase } from 'change-case';
import Iconify from 'src/components/Iconify';
import { WorkflowManagementService } from './api/WorkflowManagementService';
import { WorkflowService } from '../workflow/api/WorkflowService';
import { useSnackbar } from 'notistack';

interface Workflow {
  code: string;
  name: string;
  description: string;
  status: string;
  type: string;
}

interface Stage {
  code: string;
  name: string;
  description: string;
  order: number;
}

interface Transition {
  code: string;
  name: string;
  description: string;
  fromStages: Stage;
  toStages: Stage;
  metadata: string;
  pre_conditions: string;
  post_conditions: string;
  required_acls: string;
}

interface DetailItemProps {
  label: string;
  value?: string | null;
}

const DetailItem = ({ label, value }: DetailItemProps) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
      {label}
    </Typography>
    <Typography variant="body2">{value || 'N/A'}</Typography>
  </Grid>
);

const stageInitialState: Stage = {
  code: '',
  name: '',
  description: '',
  order: 0,
};

const transitionInitialState: Transition = {
  code: '',
  name: '',
  description: '',
  fromStages: { code: '', name: '', description: '', order: 0 },
  toStages: { code: '', name: '', description: '', order: 0 },
  metadata: '{}',
  pre_conditions: '{}',
  post_conditions: '{}',
  required_acls: '[]',
};

export default function WorkflowDetails() {
  const { code } = useParams<{ code: string }>();
  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [stages, setStages] = useState<Stage[]>([]);
  const [transitions, setTransitions] = useState<Transition[]>([]);
  const [openStageDialog, setOpenStageDialog] = useState(false);
  const [openTransitionDialog, setOpenTransitionDialog] = useState(false);
  const [transitionInForm, setTransitionInForm] = useState<Transition>(transitionInitialState);
  const [stageInForm, setStageInForm] = useState<Stage>(stageInitialState);
  const [editingStage, setEditingStage] = useState<Stage | null>(null);
  const [loading, setLoading] = useState(true);
  const [stageMenuAnchorEl, setStageMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedStageForMenu, setSelectedStageForMenu] = useState<Stage | null>(null);
  const [openChecklistsDialog, setOpenChecklistsDialog] = useState(false);
  const [activeStageForChecklists, setActiveStageForChecklists] = useState<Stage | null>(null);
  const [stageChecklists, setStageChecklists] = useState<any[]>([]);
  const [availableChecklists, setAvailableChecklists] = useState<any[]>([]);
  const [loadingChecklists, setLoadingChecklists] = useState(false);
  const [selectedChecklistCode, setSelectedChecklistCode] = useState('');
  const [checklistViewOrder, setChecklistViewOrder] = useState<number>(1);
  const [checklistEnforceOrder, setChecklistEnforceOrder] = useState<number>(0);
  const { enqueueSnackbar } = useSnackbar();

  const fetchWorkflow = useCallback(async () => {
    if (code) {
      try {
        setLoading(true);
        const [workflowResponse, stagesResponse, transitionsResponse] = await Promise.all([
          WorkflowService.getWorkflowByCode(code),
          WorkflowManagementService.getStages(code),
          WorkflowManagementService.getWorkflowTransitions(code),
        ]);
        setWorkflow(workflowResponse);
        if (Array.isArray(stagesResponse)) {
          stagesResponse.sort((a, b) => a.order - b.order);
        }
        setStages(stagesResponse);
        setTransitions(transitionsResponse);
      } catch (error) {
        console.error('Failed to fetch workflow', error);
      } finally {
        setLoading(false);
      }
    }
  }, [code]);

  useEffect(() => {
    fetchWorkflow();
  }, [fetchWorkflow]);

  const handleOpenStageDialog = (stage?: Stage) => {
    if (stage) {
      setEditingStage(stage);
      setStageInForm(stage);
    } else {
      setEditingStage(null);
      const newOrder = stages.length > 0 ? Math.max(...stages?.map((s) => s.order)) + 1 : 1;
      setStageInForm({ ...stageInitialState, order: newOrder });
    }
    setOpenStageDialog(true);
  };

  const handleCloseStageDialog = () => {
    setOpenStageDialog(false);
    setStageInForm(stageInitialState);
    setEditingStage(null);
  };

  const handleStageInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setStageInForm((prev) => ({ ...prev, [name]: name === 'order' ? parseInt(value, 10) : value }));
  };

  const handleSaveStage = async () => {
    if (!code) return;
    try {
      if (editingStage) {
        await WorkflowManagementService.updateStage(code, editingStage.code, stageInForm);
        enqueueSnackbar('Stage updated successfully');
      } else {
        await WorkflowManagementService.createStage(code, stageInForm);
        enqueueSnackbar('Stage created successfully');
      }
      handleCloseStageDialog();
      fetchWorkflow(); // Refetch all workflow data
    } catch (error: any) {
      console.error('Failed to save stage', error);
      const errorMessage = error.response?.data?.response?.message || 'Failed to save stage';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleDeleteStage = async (stageCode: string) => {
    if (!code || !window.confirm('Are you sure you want to delete this stage?')) return;
    try {
      await WorkflowManagementService.deleteStage(code, stageCode);
      enqueueSnackbar('Stage deleted successfully');
      fetchWorkflow(); // Refetch all workflow data
    } catch (error: any) {
      console.error('Failed to delete stage', error);
      const errorMessage = error.response?.data?.response?.message || 'Failed to delete stage';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleStageMenuOpen = (event: MouseEvent<HTMLButtonElement>, stage: Stage) => {
    setStageMenuAnchorEl(event.currentTarget);
    setSelectedStageForMenu(stage);
  };

  const handleStageMenuClose = () => {
    setStageMenuAnchorEl(null);
    setSelectedStageForMenu(null);
  };

  const handleStageEditFromMenu = () => {
    const stageToEdit = selectedStageForMenu;
    handleStageMenuClose();
    if (stageToEdit) {
      handleOpenStageDialog(stageToEdit);
    }
  };

  const handleStageDeleteFromMenu = () => {
    const stageToDelete = selectedStageForMenu;
    handleStageMenuClose();
    if (stageToDelete) {
      handleDeleteStage(stageToDelete.code);
    }
  };

  const handleManageChecklistsFromMenu = () => {
    const stageForChecklists = selectedStageForMenu;
    handleStageMenuClose();
    if (stageForChecklists) {
      handleOpenChecklistsDialog(stageForChecklists);
    }
  };

  const loadStageChecklists = useCallback(async (stageCode: string) => {
    if (!code) return;
    try {
      setLoadingChecklists(true);
      let stageChecklistsRes: any[] = [];
      stageChecklistsRes = await WorkflowManagementService.getStageChecklists(code, stageCode);

      const list = Array.isArray(stageChecklistsRes) ? stageChecklistsRes : [];
      setStageChecklists(list);
      const nextOrder = list.length > 0 ? Math.max(...list.map((r: any) => r.view_order || 0)) + 1 : 1;
      setChecklistViewOrder(nextOrder);
    } catch (error) {
      console.error('Failed to load stage checklists', error);
      enqueueSnackbar('Failed to load stage checklists', { variant: 'error' });
    } finally {
      setLoadingChecklists(false);
    }
  }, [code, enqueueSnackbar]);

  const loadAllChecklists = useCallback(async () => {
    try {
      const res = await WorkflowManagementService.getAllChecklist();
      setAvailableChecklists(Array.isArray(res) ? res : []);
    } catch (error) {
      console.error('Failed to load all checklists', error);
    }
  }, []);

  const handleOpenChecklistsDialog = (stage: Stage) => {
    setActiveStageForChecklists(stage);
    setSelectedChecklistCode('');
    setChecklistEnforceOrder(0);
    setOpenChecklistsDialog(true);
    loadStageChecklists(stage.code);
    loadAllChecklists();
  };

  const handleCloseChecklistsDialog = () => {
    setOpenChecklistsDialog(false);
    setActiveStageForChecklists(null);
    setStageChecklists([]);
    setSelectedChecklistCode('');
  };

  const handleAddStageChecklist = async () => {
    if (!code || !activeStageForChecklists || !selectedChecklistCode) return;
    try {
      const payload = {
        checklistCode: selectedChecklistCode,
        view_order: Number(checklistViewOrder) || 1,
        enforce_order: Number(checklistEnforceOrder) || 0,
      };
      await WorkflowManagementService.addStageChecklist(code, activeStageForChecklists.code, payload);

      enqueueSnackbar('Checklist linked to stage successfully');
      setSelectedChecklistCode('');
      loadStageChecklists(activeStageForChecklists.code);
    } catch (error: any) {
      console.error('Failed to link checklist to stage', error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.response?.message || 'Failed to link checklist';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleDeleteStageChecklist = async (ref: any) => {
    if (!code || !activeStageForChecklists) return;
    const checklistIdentifier = ref.checklistRef?.code || ref.id;
    if (!window.confirm('Are you sure you want to remove this checklist from this stage?')) return;
    try {
      await WorkflowManagementService.deleteStageChecklist(code, activeStageForChecklists.code, checklistIdentifier);

      enqueueSnackbar('Checklist removed from stage successfully');
      loadStageChecklists(activeStageForChecklists.code);
    } catch (error: any) {
      console.error('Failed to delete stage checklist', error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.response?.message || 'Failed to remove checklist';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const [editingTransition, setEditingTransition] = useState<Transition | null>(null);

  const handleOpenTransitionDialog = (transition?: Transition) => {
    if (transition) {
      setEditingTransition(transition);
      setTransitionInForm(transition);
    } else {
      setEditingTransition(null);
      setTransitionInForm(transitionInitialState);
    }
    setOpenTransitionDialog(true);
  };

  const handleCloseTransitionDialog = () => {
    setOpenTransitionDialog(false);
    setTransitionInForm(transitionInitialState);
    setEditingTransition(null);
  };

  const handleTransitionInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === 'fromStageCode') {
      const fromStage = stages.find((s) => s.code === value);
      if (fromStage) setTransitionInForm((prev) => ({ ...prev, fromStages: fromStage }));
    } else if (name === 'toStageCode') {
      const toStage = stages.find((s) => s.code === value);
      if (toStage) setTransitionInForm((prev) => ({ ...prev, toStages: toStage }));
    } else {
      setTransitionInForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSaveTransition = async () => {
    if (!code) return;
    try {
      if (editingTransition) {
        const { fromStages, toStages, ...rest } = transitionInForm;
        const payload = {
          ...rest,
          fromStageCode: fromStages.code,
          toStageCode: toStages.code,
        };
        await WorkflowManagementService.updateWorkflowTransition(code, editingTransition.code, payload);
        enqueueSnackbar('Transition updated successfully');
      } else {
        const { fromStages, toStages, ...rest } = transitionInForm;
        const payload = {
          ...rest,
          fromStageCode: fromStages.code,
          toStageCode: toStages.code,
        };
        await WorkflowManagementService.createWorkflowTransition(code, payload);
        enqueueSnackbar('Transition created successfully');
      }
      handleCloseTransitionDialog();
      fetchWorkflow(); // Refetch all workflow data
    } catch (error: any) {
      console.error('Failed to save transition', error);
      const errorMessage = error.response?.data?.response?.message || 'Failed to save transition';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleDeleteTransition = async (transitionCode: string) => {
    if (!code || !window.confirm('Are you sure you want to delete this transition?')) return;
    try {
      await WorkflowManagementService.deleteWorkflowTransition(code, transitionCode);
      enqueueSnackbar('Transition deleted successfully');
      fetchWorkflow(); // Refetch all workflow data
    } catch (error: any) {
      console.error('Failed to delete transition', error);
      const errorMessage = error.response?.data?.response?.message || 'Failed to delete transition';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  return (
    <Page title="Workflow Details">
      <Container>
        <HeaderBreadcrumbs
          heading=""
          title="Workflow Details"
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Workflows', href: PATH_DASHBOARD.crs.workflow },
            { name: workflow?.name || 'Details' },
          ]}
        />

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && workflow && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ flexGrow: 1 }}>
                  {workflow.name}
                </Typography>
                <Chip
                  label={capitalCase(workflow.status || 'N/A')}
                  color={workflow.status === 'active' ? 'success' : 'default'}
                  size="small"
                />
              </Box>
              <Grid container spacing={2}>
                <DetailItem label="Code" value={workflow.code} />
                <DetailItem label="Type" value={workflow.type} />
                <DetailItem label="Description" value={workflow.description} />
              </Grid>
            </CardContent>
          </Card>
        )}

        {!loading && workflow && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">
                  Stages
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Iconify icon={'eva:plus-fill'} />}
                  onClick={() => handleOpenStageDialog()}
                >
                  New Stage
                </Button>
              </Box>
              {stages.length > 0 && (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Order</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Code</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stages?.map((stage) => (
                        <TableRow key={stage.code}>
                          <TableCell>{stage.order}</TableCell>
                          <TableCell>{stage.name}</TableCell>
                          <TableCell>
                            <Link component="button" variant="body2" onClick={() => handleOpenStageDialog(stage)}>
                              {stage.code}
                            </Link>
                          </TableCell>
                          <TableCell>{stage.description}</TableCell>
                          <TableCell align="right">
                            <IconButton onClick={(e) => handleStageMenuOpen(e, stage)}>
                              <Iconify icon={'eva:more-vertical-fill'} width={20} height={20} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        )}

        <Menu
          anchorEl={stageMenuAnchorEl}
          open={Boolean(stageMenuAnchorEl)}
          onClose={handleStageMenuClose}
        >
          <MenuItem onClick={handleStageEditFromMenu}>
            <Iconify icon={'eva:edit-fill'} sx={{ mr: 2, width: 20, height: 20 }} />
            Edit
          </MenuItem>
          <MenuItem onClick={handleManageChecklistsFromMenu}>
            <Iconify icon={'eva:checkmark-square-2-fill'} sx={{ mr: 2, width: 20, height: 20 }} />
            Manage Checklists
          </MenuItem>
          <MenuItem onClick={handleStageDeleteFromMenu} sx={{ color: 'error.main' }}>
            <Iconify icon={'eva:trash-2-fill'} sx={{ mr: 2, width: 20, height: 20 }} />
            Delete
          </MenuItem>
        </Menu>

        {!loading && workflow && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">
                  Transitions
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Iconify icon={'eva:plus-fill'} />}
                  onClick={() => handleOpenTransitionDialog()}
                >
                  New Transition
                </Button>
              </Box>
              {transitions.length > 0 && (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Code</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>From Stage</TableCell>
                        <TableCell>To Stage</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transitions.map((transition) => (
                        <TableRow key={transition.code}>
                          <TableCell>{transition.name}</TableCell>
                          <TableCell>
                            <Link component="button" variant="body2" onClick={() => handleOpenTransitionDialog(transition)}>
                              {transition.code}
                            </Link>
                          </TableCell>
                          <TableCell>{transition.description}</TableCell>
                          <TableCell>
                            <Tooltip title={transition.fromStages.name} arrow>
                              <span>{transition.fromStages.code}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={transition.toStages.name} arrow>
                              <span>{transition.toStages.code}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Button size="small" color="error" onClick={() => handleDeleteTransition(transition.code)}>
                              Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        )}

        <Dialog open={openStageDialog} onClose={handleCloseStageDialog} fullWidth maxWidth="sm">
          <DialogTitle>{editingStage ? 'Edit Stage' : 'New Stage'}</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <TextField
                name="code"
                label="Code"
                value={stageInForm.code}
                onChange={handleStageInputChange}
                fullWidth
                disabled={!!editingStage}
              />
              <TextField
                name="name"
                label="Name"
                value={stageInForm.name}
                onChange={handleStageInputChange}
                fullWidth
              />
              <TextField
                name="description"
                label="Description"
                value={stageInForm.description}
                onChange={handleStageInputChange}
                fullWidth
                multiline
                rows={3}
              />
              <TextField
                name="order"
                label="Order"
                type="number"
                value={stageInForm.order}
                onChange={handleStageInputChange}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseStageDialog}>Cancel</Button>
            <Button onClick={handleSaveStage} variant="contained">{editingStage ? 'Save' : 'Create'}</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openChecklistsDialog} onClose={handleCloseChecklistsDialog} fullWidth maxWidth="md">
          <DialogTitle>
            Manage Checklists - {activeStageForChecklists?.name} ({activeStageForChecklists?.code})
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                Link Checklist
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={5}>
                  <TextField
                    select
                    label="Select Checklist"
                    value={selectedChecklistCode}
                    onChange={(e) => setSelectedChecklistCode(e.target.value)}
                    fullWidth
                    size="small"
                  >
                    {availableChecklists
                      ?.filter((c) => !stageChecklists.some((s) => s.checklistRef?.code === c.code))
                      .map((chk) => (
                        <MenuItem key={chk.code} value={chk.code}>
                          {chk.name} ({chk.code})
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={2.5}>
                  <TextField
                    label="View Order"
                    type="number"
                    value={checklistViewOrder}
                    onChange={(e) => setChecklistViewOrder(parseInt(e.target.value, 10) || 0)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} sm={2.5}>
                  <TextField
                    label="Enforce Order"
                    type="number"
                    value={checklistEnforceOrder}
                    onChange={(e) => setChecklistEnforceOrder(parseInt(e.target.value, 10) || 0)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    variant="contained"
                    onClick={handleAddStageChecklist}
                    disabled={!selectedChecklistCode}
                    fullWidth
                    startIcon={<Iconify icon={'eva:plus-fill'} />}
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Assigned Checklists
            </Typography>
            {loadingChecklists ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : stageChecklists.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No checklists assigned to this stage.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>View Order</TableCell>
                      <TableCell>Enforce Order</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stageChecklists.map((ref) => (
                      <TableRow key={ref.id}>
                        <TableCell>{ref.view_order ?? '-'}</TableCell>
                        <TableCell>{ref.enforce_order ?? '-'}</TableCell>
                        <TableCell>{ref.checklistRef?.code || '-'}</TableCell>
                        <TableCell>{ref.checklistRef?.name || '-'}</TableCell>
                        <TableCell>{ref.checklistRef?.description || '-'}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleDeleteStageChecklist(ref)}
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseChecklistsDialog}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openTransitionDialog} onClose={handleCloseTransitionDialog} fullWidth maxWidth="sm">
          <DialogTitle>{editingTransition ? 'Edit Transition' : 'New Transition'}</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <TextField
                name="code"
                label="Code"
                value={transitionInForm.code}
                onChange={handleTransitionInputChange}
                fullWidth
                disabled={!!editingTransition}
              />
              <TextField
                name="name"
                label="Name"
                value={transitionInForm.name}
                onChange={handleTransitionInputChange}
                fullWidth
              />
              <TextField
                name="description"
                label="Description"
                value={transitionInForm.description}
                onChange={handleTransitionInputChange}
                fullWidth
                multiline
                rows={3}
              />
              <TextField
                name="pre_conditions"
                label="Pre Conditions"
                value={transitionInForm.pre_conditions}
                onChange={handleTransitionInputChange}
                fullWidth
                multiline
                rows={3}
              />
              <TextField
                name="post_conditions"
                label="Post Conditions"
                value={transitionInForm.post_conditions}
                onChange={handleTransitionInputChange}
                fullWidth
                multiline
                rows={3}
              />
              <TextField
                name="metadata"
                label="Metadata"
                value={transitionInForm.metadata}
                onChange={handleTransitionInputChange}
                fullWidth
                multiline
                rows={3}
              />
              <TextField
                name="required_acls"
                label="Required ACLs"
                value={transitionInForm.required_acls}
                onChange={handleTransitionInputChange}
                fullWidth
                multiline
                rows={3}
              />
              <TextField
                name="fromStageCode"
                select
                label="From Stage"
                value={transitionInForm.fromStages.code}
                onChange={handleTransitionInputChange}
                fullWidth
              >
                {stages?.map((stage) => (
                  <MenuItem key={stage.code} value={stage.code}>
                    {stage.name} ({stage.code})
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                name="toStageCode"
                select
                label="To Stage"
                value={transitionInForm.toStages.code}
                onChange={handleTransitionInputChange}
                fullWidth
              >
                {stages?.map((stage) => (
                  <MenuItem key={stage.code} value={stage.code}>
                    {stage.name} ({stage.code})
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseTransitionDialog}>Cancel</Button>
            <Button onClick={handleSaveTransition} variant="contained">
              {editingTransition ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}